import axios from "axios";
import {
    GET_STEP_ASSIGNED_SIGNERS_API_URL,
    GET_TEMPLATE_ENTITY_TYPE_SIGNERS_URL,
    GET_TEMPLATE_WORKFLOW_URL,
    GET_WORKFLOWS_ASSIGNED_TO_ENTITY_TYPE_TEMPLATE_URL,
    GET_WORKFLOW_ENTITIES_FOR_ASSIGNMENT_URL,
    get_request_config
} from "@/utils/system_constants/constant";
import { parse_response_data } from "./configure_signers_helpers";
import {
    apply_loaded_signer_configuration,
    is_valid_workflow_step,
    normalize_entity_profile_results,
    normalize_user_options,
    normalize_workflow_step,
    sort_workflow_steps
} from "./configure_signers_load_support";

function is_failed_response(parsed_response) {
    return parsed_response?.status && parsed_response.status !== "success";
}
function get_signer_configuration_load_error(error) {
    return error?.message || "Failed to load signer configuration.";
}
function get_step_entity_type_id(step) {
    return step.entity_type_id;
}
function get_user_options_error(role_entity_type_id) {
    return `Failed to load users for entity type ${role_entity_type_id}.`;
}
function get_assigned_signer_error(step) {
    return `Failed to load assigned signers for ${step.entity_type_name}.`;
}
function set_load_error(vm, message) {
    vm.load_error = message;
}
function show_system_loader(message) {
    window.dispatchEvent(new CustomEvent("system-loader:show", {
        detail: message
    }));
}
function hide_system_loader_after_render(vm) {
    vm.$nextTick(() => {
        window.dispatchEvent(new CustomEvent("system-loader:hide"));
    });
}
function get_workflow_steps_error(workflow_steps, vm) {
    if (workflow_steps.length) {
        return "";
    }
    set_load_error(vm, "No workflow steps are configured for this template.");
    vm.show_error(vm.load_error);
    return vm.load_error;
}
function validate_signer_configuration_context(vm) {
    if (!vm.selected_template) {
        set_load_error(vm, "Select a template before configuring signers.");
        return false;
    }
    if (!vm.template_document_entity_type_id) {
        set_load_error(vm, "The selected template is missing its entity-type assignment reference.");
        return false;
    }
    if (!vm.entity_profile_id) {
        set_load_error(vm, "No entity profile was provided for signer assignment.");
        return false;
    }
    return true;
}
async function load_entity_type_user_options(vm, role_entity_type_id) {
    const response = await axios.get(
        GET_WORKFLOW_ENTITIES_FOR_ASSIGNMENT_URL,
        get_request_config({ params: { entity_type_id: role_entity_type_id, entity_profile_id: vm.entity_profile_id } })
    );
    const parsed_response = parse_response_data(response.data);
    if (is_failed_response(parsed_response)) {
        vm.show_error(parsed_response?.message || get_user_options_error(role_entity_type_id));
        return [role_entity_type_id, []];
    }
    const entity_profiles = normalize_entity_profile_results(parsed_response);
    const user_options = normalize_user_options(entity_profiles);
    return [role_entity_type_id, user_options];
}
async function load_assigned_signers_for_step(vm, workflow_assignment_id, step) {
    const response = await axios.get(
        GET_STEP_ASSIGNED_SIGNERS_API_URL,
        get_request_config({ params: { workflow_assignment_id, entity_profile_id: vm.entity_profile_id, step_id: step.id } })
    );
    const parsed_response = parse_response_data(response.data);
    if (is_failed_response(parsed_response)) {
        vm.show_error(parsed_response?.message || get_assigned_signer_error(step));
        return [step.id, []];
    }
    const assigned_signers = Array.isArray(parsed_response?.data) ? parsed_response.data : [];
    return [step.id, assigned_signers];
}
async function load_workflow_data(vm) {
    const signer_config = await vm.fetch_signer_config();
    if (!signer_config) {
        set_load_error(vm, "Failed to load signer configuration.");
        return null;
    }
    const workflow_assignment = await vm.fetch_workflow_assignment();
    if (!workflow_assignment) {
        set_load_error(vm, "No active workflow assignment was found for the selected template.");
        return null;
    }
    const workflow_details = await vm.fetch_workflow_details(workflow_assignment.workflow);
    if (!workflow_details) {
        set_load_error(vm, "Failed to load workflow details.");
        return null;
    }
    const workflow_data = { signer_config, workflow_assignment, workflow_details };
    return workflow_data;
}
function resolve_workflow_steps(vm, workflow_details) {
    const workflow_steps = vm.normalize_workflow_steps(workflow_details?.data?.steps);
    const workflow_steps_error = get_workflow_steps_error(workflow_steps, vm);
    return workflow_steps_error ? null : workflow_steps;
}
async function load_workflow_steps(vm) {
    const workflow_data = await load_workflow_data(vm);
    if (!workflow_data) {
        return null;
    }
    const workflow_steps = resolve_workflow_steps(vm, workflow_data.workflow_details);
    if (!workflow_steps) {
        return null;
    }
    const loaded_workflow = { ...workflow_data, workflow_steps };
    return loaded_workflow;
}
async function load_signer_configuration_dependencies(vm, workflow_assignment, workflow_steps) {
    const user_options_by_entity_type = await vm.fetch_user_options_by_entity_type(workflow_steps);
    const assigned_signers_by_step = await vm.fetch_assigned_signers_by_step(workflow_assignment.id, workflow_steps);
    const dependencies = { user_options_by_entity_type, assigned_signers_by_step };
    return dependencies;
}

export const fetch_signer_config = async (vm) => {
    const response = await axios.get(
        GET_TEMPLATE_ENTITY_TYPE_SIGNERS_URL,
        get_request_config({ params: { template_document_entity_type_id: vm.template_document_entity_type_id } })
    );
    const parsed = parse_response_data(response.data);
    if (parsed?.status && parsed.status !== "success") {
        vm.show_error(parsed?.message || "Failed to load signer configuration.");
        return null;
    }
    return parsed;
};
export const fetch_workflow_assignment = async (vm) => {
    const response = await axios.post(
        GET_WORKFLOWS_ASSIGNED_TO_ENTITY_TYPE_TEMPLATE_URL,
        { template_document_entity_type_id: vm.template_document_entity_type_id },
        get_request_config()
    );
    const parsed = parse_response_data(response.data);
    const assignments = Array.isArray(parsed?.data) ? parsed.data : [];
    if (!assignments.length) {
        vm.show_error("No active workflow assignment was found for the selected template.");
        return null;
    }
    return assignments[0];
};
export const fetch_workflow_details = async (vm, workflow_id) => {
    const response = await axios.post(GET_TEMPLATE_WORKFLOW_URL, { workflow_id }, get_request_config());
    const parsed = parse_response_data(response.data);
    if (parsed?.status && parsed.status !== "success") {
        vm.show_error(parsed?.message || "Failed to load workflow details.");
        return null;
    }
    return parsed;
};
export const normalize_workflow_steps = (steps) => {
    if (!Array.isArray(steps)) {
        return [];
    }
    const normalized_steps = steps.map(function map_workflow_step(step, index) {
        return normalize_workflow_step(step, index);
    }).filter(function filter_invalid_step(step) {
        return is_valid_workflow_step(step);
    }).sort(function sort_steps(first_step, second_step) {
        return sort_workflow_steps(first_step, second_step);
    });
    return normalized_steps;
};
export const fetch_user_options_by_entity_type = async (vm, workflow_steps) => {
    const entity_type_ids = [...new Set(workflow_steps.map(function map_entity_type_id(step) {
        return get_step_entity_type_id(step);
    }).filter(Boolean))];
    const entries = await Promise.all(entity_type_ids.map(function load_entity_type_entry(role_entity_type_id) {
        return load_entity_type_user_options(vm, role_entity_type_id);
    }));
    return Object.fromEntries(entries);
};
export const fetch_assigned_signers_by_step = async (vm, workflow_assignment_id, workflow_steps) => {
    const results = await Promise.all(workflow_steps.map(function load_step_entry(step) {
        return load_assigned_signers_for_step(vm, workflow_assignment_id, step);
    }));
    return Object.fromEntries(results);
};
export const load_signer_configuration = async (vm) => {
    vm.reset_runtime_state();
    if (!validate_signer_configuration_context(vm)) {
        return;
    }
    vm.loading = true;
    vm.set_busy_state(true);
    show_system_loader("Loading signer configuration...");
    try {
        const loaded_workflow = await load_workflow_steps(vm);
        if (!loaded_workflow) {
            return;
        }
        const { signer_config, workflow_assignment, workflow_details, workflow_steps } = loaded_workflow;
        const { user_options_by_entity_type, assigned_signers_by_step } = await load_signer_configuration_dependencies(vm, workflow_assignment, workflow_steps);
        apply_loaded_signer_configuration(vm, signer_config, workflow_assignment, workflow_details, workflow_steps, assigned_signers_by_step, user_options_by_entity_type);
    } catch (error) {
        vm.load_error = get_signer_configuration_load_error(error);
        vm.show_error(vm.load_error);
    } finally {
        vm.loading = false;
        vm.set_busy_state(vm.saving);
        hide_system_loader_after_render(vm);
    }
};
